import React from "react";
import { Search, Share2, PenTool, BarChart3, TrendingUp, Monitor, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const services = [{
  title: "Strategic Partner Identification",
  description: "We leverage deep market data to connect you with vetted international partners that align with your specific business objectives.",
  icon: Search,
  pattern: "dots",
  link: "/TradeServicesPage"
}, {
  title: "Global Supplier Networking",
  description: "Access an exclusive ecosystem of verified suppliers across key manufacturing hubs to diversify and strengthen your supply chain.",
  icon: Share2,
  pattern: "grid",
  link: "/TradeServicesPage"
}, {
  title: "Contractual Framework Design",
  description: "Expert guidance on drafting cross-border agreements that ensure legal compliance and protect your long-term commercial interests.",
  icon: PenTool,
  pattern: "lines",
  link: "/RegulatoryPage"
}, {
  title: "Procurement Performance Audits",
  description: "Analyze your procurement cycles with data-driven insights to identify significant cost-saving opportunities and efficiency gains.",
  icon: BarChart3,
  pattern: "dots",
  link: "/TradeServicesPage"
}, {
  title: "Scalable Sourcing Strategies",
  description: "Future-proof your operations with adaptive sourcing models designed to navigate increasingly volatile global market landscapes.",
  icon: TrendingUp,
  pattern: "grid",
  link: "/MarketEntryPage"
}, {
  title: "Digital Supply Chain Integration",
  description: "Modernize your logistics with integrated cloud-based tracking and procurement tools for real-time visibility and total control.",
  icon: Monitor,
  pattern: "lines",
  link: "/TradeServicesPage"
}];
export default function OurServices() {
  return <section data-section-id="360" id="partner-supplier-matchmaking" className="bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-40 mix-blend-multiply" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/10 rounded-full blur-3xl opacity-40 mix-blend-multiply" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
            Partner & Supplier Matchmaking
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl">
            Precision-engineered connection services for global expansion. We bridge the gap between your operational needs and world-class trade resources.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => <div key={i} data-index={i} className="h-full">
              <ServiceCard service={service} />
            </div>)}
        </div>
      </div>
    </section>;
}
function ServiceCard({
  service
}: {
  service: typeof services[0];
}) {
  const Icon = service.icon;
  const patternStyles = {
    dots: "bg-[radial-gradient(var(--primary)_1px,transparent_1px)] [background-size:20px_20px] opacity-[0.05]",
    grid: "bg-[linear-gradient(to_right,var(--primary)_1px,transparent_1px),linear-gradient(to_bottom,var(--primary)_1px,transparent_1px)] [background-size:40px_40px] opacity-[0.03]",
    lines: "bg-[repeating-linear-gradient(45deg,var(--primary)_0,var(--primary)_1px,transparent_0,transparent_10px)] opacity-[0.03]"
  };
  return <Card className="group relative h-full overflow-hidden border-border/60 bg-card backdrop-blur-sm hover:border-secondary/50 transition-all duration-500 shadow-sm hover:shadow-xl">
      {}
      <div className={`absolute inset-0 pointer-events-none ${patternStyles[service.pattern as keyof typeof patternStyles]}`} />
      
      {}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      <CardHeader className="relative z-10 pb-2">
        <div className="mb-4 inline-flex p-3 rounded-xl bg-primary/5 text-primary ring-1 ring-primary/10 group-hover:ring-secondary/30 transition-all duration-300">
          <Icon className="size-6 animate-pulse" style={{
          animationDuration: '6s'
        }} />
        </div>
        <CardTitle className="text-xl md:text-2xl font-bold tracking-tight group-hover:text-secondary transition-colors duration-300">
          {service.title}
        </CardTitle>
      </CardHeader>

      <CardContent className="relative z-10 pb-20">
        <CardDescription className="text-base leading-relaxed text-muted-foreground">
          {service.description}
        </CardDescription>
      </CardContent>

      {}
      <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out z-20 bg-gradient-to-t from-card via-card to-transparent pt-12">
        <Button asChild className="w-full shadow-md shadow-primary/10 bg-primary text-primary-foreground hover:bg-secondary hover:text-secondary-foreground" size="lg">
          <Link to={service.link}>
            Explore Details <ArrowRight className="ml-2 size-4" />
          </Link>
        </Button>
      </div>
    </Card>;
}